import React, { useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useCallbackObservable, useObservable } from 'react-use-observable';

import { Input, Select, Row, Col } from 'antd';
import type { InputRef } from 'antd';

import MyModal from 'components/MyModal';
import Spin from 'components/Spin';
import { tap } from 'rxjs/operators';
import type { User } from 'v2/interfaces/access-control';
import accessControlService from 'v2/services/access-control';

type Props = {
  current?: User;
  onComplete: () => void;
  onCancel: () => void;
};

export default function FormUser({ current, onComplete, onCancel }: Props) {
  const [form, setForm] = useState({
    name: current?.name || '',
    email: current?.email || '',
    group: current?.group_id || (undefined as number | undefined)
  });
  const [isLoadingGroups] = useObservable(() => accessControlService.isLoadingGroups$, []);
  const [groups] = useObservable(() => accessControlService.getGroups(), []);
  const [isSaving, setIsSaving] = useState(false);
  const [saveUser] = useCallbackObservable(
    data =>
      accessControlService.saveUser(data).pipe(
        tap(
          () => {
            toast.success('Sucesso, usuário criado!');
            onComplete();
            setIsSaving(false);
          },
          err => {
            toast.error(err);
            setIsSaving(false);
          }
        )
      ),
    []
  );
  const [updateUser] = useCallbackObservable(
    data =>
      accessControlService.updateUser(data).pipe(
        tap(
          () => {
            toast.success('Sucesso, usuário atualizado!');
            onComplete();
            setIsSaving(false);
          },
          err => {
            toast.error(err);
            setIsSaving(false);
          }
        )
      ),
    []
  );

  const nameInputRef = useRef<InputRef>(null);
  const emailInputRef = useRef<InputRef>(null);

  function handleChange(field: string, value: string | number) {
    setForm(old => ({
      ...old,
      [field]: value
    }));
  }

  const isEditing = !!current;

  function handleSubmit() {
    setIsSaving(true);

    if (!form.name) {
      nameInputRef.current?.focus();
      toast.error('O nome do usuário é obrigatório.');
      setIsSaving(false);
      return;
    }

    const isEmail = /\S+@\S+\.\S+/.test(form.email);

    if (!isEmail) {
      emailInputRef.current?.focus();
      toast.error('O email do usuário é inválido.');
      setIsSaving(false);
      return;
    }

    if (!form.group) {
      toast.error('Selecione um grupo.');
      setIsSaving(false);
      return;
    }

    if (isEditing) {
      updateUser({
        id: current.id,
        oldGroup: current.group_id,
        ...form
      });
      return;
    }

    saveUser(form);
  }

  const groupOptions = useMemo(() => {
    return (groups || []).map(group => ({ value: group.id, label: group.name }));
  }, [groups]);

  return (
    <MyModal
      hasActive
      title={isEditing ? 'Atualizar usuário' : 'Novo usuário'}
      subTitle={isEditing ? 'Atualize o grupo desse usuário' : 'Adicionar usuários aos grupos.'}
      closeModal={onCancel}
      textButtonOk={isEditing ? 'Atualizar' : 'Adicionar'}
      hasButtons
      handleClickOk={handleSubmit}
      loading={isSaving || isLoadingGroups}
    >
      {isLoadingGroups && <Spin />}

      {!isLoadingGroups && (
        <>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12}>
              <div>
                <label className='mb-1 block text-sm text-gray-600'>Email*</label>
                <Input
                  ref={emailInputRef}
                  name='email'
                  value={form.email}
                  disabled={isSaving || isEditing}
                  onChange={e => handleChange('email', e.target.value)}
                  placeholder='Email'
                />
                {isEditing && <span className='text-xs text-gray-400'>Não é possível editar</span>}
              </div>
            </Col>
            <Col xs={24} sm={12}>
              <div>
                <label className='mb-1 block text-sm text-gray-600'>Nome*</label>
                <Input
                  ref={nameInputRef}
                  name='name'
                  value={form.name}
                  disabled={isSaving || isEditing}
                  onChange={e => handleChange('name', e.target.value)}
                  placeholder='Nome'
                />
                {isEditing && <span className='text-xs text-gray-400'>Não é possível editar</span>}
              </div>
            </Col>
          </Row>

          <div style={{ marginTop: 16 }}>
            <label className='mb-1 block text-sm text-gray-600'>Grupo</label>
            <Select
              value={form.group}
              disabled={isSaving}
              onChange={value => handleChange('group', value)}
              className='w-full'
              placeholder='Selecione o grupo'
              options={groupOptions}
            />
          </div>
        </>
      )}
    </MyModal>
  );
}
